/**
 * Freedom Funding Readiness™ Resource Shop
 * Full toolkits + featured operations + bundles.
 * Every purchase button opens the license modal before Stripe checkout.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Compass, Layers, Target, Users2, Award, Heart, Wrench,
  Sparkles, TrendingUp, Package, Star, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useSiteContent } from "@/lib/siteContent";
import { FfrNav, FfrFoot, PurchaseButton, FFR } from "./FundingReadiness";

const TOOLKITS = [
  { key: "funding",     Icon: Compass,   name: "Funding Readiness Toolkit", scKey: "ffr_stripe_toolkit_funding",
    includes: ["7-Stage Readiness Framework", "Self-Assessment Scorecard", "Funder Priorities Matrix", "Quick-Start Action Plan"] },
  { key: "evidence",    Icon: Target,    name: "Evidence Bank Toolkit",     scKey: "ffr_stripe_toolkit_evidence",
    includes: ["Outcomes Tracker", "Data Collection Templates", "Evidence Organizer", "Impact Story Framework"] },
  { key: "program",     Icon: Layers,    name: "Program Blueprint Toolkit", scKey: "ffr_stripe_toolkit_program",
    includes: ["Program Design Template", "Theory of Change Canvas", "Logic Model Builder", "Program Budget Worksheet"] },
  { key: "board",       Icon: Users2,    name: "Board Governance Toolkit",  scKey: "ffr_stripe_toolkit_board",
    includes: ["Board Policies Pack", "Meeting Agenda Templates", "Governance Calendar", "Board Onboarding Checklist"] },
  { key: "grant",       Icon: Award,     name: "Grant Success Toolkit",     scKey: "ffr_stripe_toolkit_grant",
    includes: ["Application Checklist", "Grant Budget Template", "Reviewer Scorecard", "Follow-Up Report Template"] },
  { key: "problem",     Icon: FileText,  name: "Grant Problem Statement Template", scKey: "ffr_stripe_toolkit_problem_statement",
    includes: ["Fillable Problem Statement Template", "Funder-Ready Language Bank", "Data-Backed Statement Examples", "Reviewer-Focused Structure Guide"] },
  { key: "fundraising", Icon: Heart,     name: "Fundraising Toolkit",       scKey: "ffr_stripe_toolkit_fundraising",
    includes: ["Donor Journey Map", "Gift Chart & Ask Ladder", "Campaign Timeline", "Thank-You Templates"] },
];

const BUNDLES = [
  { key: "choose3",  price: "$67",  name: "Choose Any Three",          desc: "Pick any three toolkits — mix and match.", scKey: "ffr_stripe_bundle_choose3",  badge: null,
    includes: ["Any 3 of the $27 toolkits", "Individual license on each", "Instant download access", "Saves $14 vs. buying separately"] },
  { key: "funding",  price: "$147", name: "Funding Readiness Bundle",  desc: "Funding + Evidence + Program + Grant Success.", scKey: "ffr_stripe_bundle_funding",  badge: null,
    includes: ["Funding Readiness Toolkit", "Evidence Bank Toolkit", "Program Blueprint Toolkit", "Grant Success Toolkit"] },
  { key: "ultimate", price: "$197", name: "Ultimate Bundle",           desc: "All 7 core toolkits + Operations flagship.", scKey: "ffr_stripe_bundle_ultimate", badge: "Best Value",
    includes: ["All 7 core toolkits", "Nonprofit Operations Toolkit ($97 value)", "Every dashboard + framework", "Best value — saves $89"] },
];

const OPERATIONS_INCLUDES = [
  "Operations Dashboard", "Executive Dashboard", "Meeting Dashboard", "KPI Dashboard",
  "Operations Framework", "Workflow Maps", "Event Framework", "Operations Manual",
  "Grant Workflow", "Volunteer Workflow", "Board Workflow",
];

/* ---------- Small header ---------- */
function ShopHero() {
  return (
    <section
      className="pt-32 lg:pt-40 pb-16 lg:pb-20 relative overflow-hidden"
      data-testid="shop-hero"
      style={{ background: `linear-gradient(180deg, ${FFR.forestDeep} 0%, ${FFR.forest} 100%)` }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: FFR.gold }} />
      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: FFR.gold }}>Resource Shop</div>
        <h1
          className="font-black uppercase leading-[0.98] tracking-[-0.02em] text-white"
          style={{ fontSize: "clamp(38px, 6vw, 72px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}
        >
          Toolkits that make you <span style={{ color: FFR.gold }}>funding ready.</span>
        </h1>
        <p className="mt-5 text-white/80 text-[16px] leading-[1.65] max-w-2xl">
          Seven focused $27 toolkits, an Operations flagship at $97, and value-driven bundles from $67 to $197. Each purchase is licensed to one individual and comes with a receipt.
        </p>
        <div className="mt-6 inline-flex items-start gap-2.5 rounded-xl px-4 py-3 border" data-testid="shop-email-hint" style={{ background: "rgba(201,167,90,0.08)", borderColor: `${FFR.gold}55`, maxWidth: 620 }}>
          <div className="text-[10px] uppercase font-black tracking-[0.2em] mt-0.5 flex-shrink-0" style={{ color: FFR.gold }}>Tip</div>
          <div className="text-[13px] leading-[1.55] text-white/85">
            Use the <strong>same email at Stripe checkout and at your Library signup</strong> so your toolkits show up automatically in your account. <Link to="/library/signup" className="underline font-semibold" style={{ color: FFR.gold }}>Create your library account &rarr;</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Individual toolkits grid ---------- */
function ToolkitsGrid() {
  const sc = useSiteContent();
  return (
    <section className="py-20 lg:py-24" data-testid="shop-toolkits" style={{ background: FFR.ivory }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>Individual Toolkits</div>
            <h2 className="font-black uppercase leading-[1.05] tracking-[-0.02em]" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
              Seven focused toolkits · <span style={{ color: FFR.gold }}>$27 each</span>
            </h2>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOOLKITS.map((t) => {
            const url = (sc[t.scKey] || "").trim();
            return (
              <ToolkitCard key={t.key} Icon={t.Icon} name={t.name} price="$27" stripeUrl={url} testid={`shop-toolkit-${t.key}`} includes={t.includes} />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ToolkitCard({ Icon, name, price, stripeUrl, testid, includes = [] }) {
  const available = !!stripeUrl;
  return (
    <div
      className="rounded-2xl p-7 border-2 relative flex flex-col"
      style={{ background: "white", borderColor: `${FFR.gold}33` }}
      data-testid={testid}
    >
      <div className="absolute top-0 left-0 h-1.5 w-full rounded-t-2xl" style={{ background: FFR.gold }} />
      <div className="w-12 h-12 rounded-xl grid place-items-center mb-4" style={{ background: FFR.goldSoft }}>
        <Icon size={20} style={{ color: FFR.forest }} strokeWidth={2.1} />
      </div>
      <h3 className="font-black text-[19px] tracking-[-0.02em] leading-tight" style={{ color: FFR.forest }}>{name}</h3>
      <div className="mt-4 font-black text-[36px] leading-none" style={{ color: FFR.forestDeep, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>{price}</div>
      <div className="text-[12px] mt-1 mb-5" style={{ color: `${FFR.forestDeep}99` }}>One-time · individual license</div>

      {includes.length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-2" style={{ color: FFR.gold }}>What&rsquo;s included</div>
          <ul className="space-y-1.5 text-[13px]" style={{ color: FFR.forestDeep }}>
            {includes.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: FFR.gold }} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto">
        <PurchaseButton stripeUrl={stripeUrl} available={available} testid={`${testid}-cta`} label="Add to cart" productName={name} />
      </div>
    </div>
  );
}

/* ---------- Featured operations product ---------- */
function OperationsFlagship() {
  const sc = useSiteContent();
  const stripeUrl = (sc.ffr_stripe_toolkit_operations || "").trim();
  const available = !!stripeUrl;
  return (
    <section className="py-20 lg:py-24" data-testid="shop-operations" style={{ background: FFR.parchment }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6 rounded-3xl p-8 lg:p-10 relative shadow-xl" style={{ background: "white", border: `2px solid ${FFR.gold}44` }}>
            <div className="absolute -top-3 left-6 z-10">
              <div className="text-[10px] uppercase font-black tracking-[0.22em] px-3 py-1.5 rounded shadow-lg" style={{ background: FFR.gold, color: FFR.forestDeep }}>Most Popular</div>
            </div>
            <div className="pt-3 text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Featured — Flagship</div>
            <h2 className="font-black uppercase mt-2 leading-tight tracking-[-0.02em]" style={{ fontSize: "clamp(28px, 3.4vw, 40px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
              Nonprofit Operations <span style={{ color: FFR.gold }}>Toolkit</span>
            </h2>
            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-black text-[54px] leading-none" style={{ color: FFR.forestDeep }}>$97</span>
              <span className="text-[13px]" style={{ color: `${FFR.forestDeep}99` }}>Individual license</span>
            </div>
            <p className="mt-4 text-[15px]" style={{ color: FFR.forestDeep }}>Common dashboard, framework, workflow, and manual you need to run a nonprofit as the funders expect.</p>

            <div className="mt-6">
              <PurchaseButton stripeUrl={stripeUrl} available={available} testid="shop-operations-cta" label="Add to cart" productName="Nonprofit Operations Toolkit" />
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: FFR.gold }}>What&rsquo;s included</div>
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-4 text-[14px]" style={{ color: FFR.forestDeep }}>
              {OPERATIONS_INCLUDES.map((i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: FFR.gold }} />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Bundles ---------- */
function Bundles() {
  const sc = useSiteContent();
  const [choose3Open, setChoose3Open] = useState(false);
  const choose3Url = (sc["ffr_stripe_bundle_choose3"] || "").trim();
  return (
    <section className="py-20 lg:py-24" data-testid="shop-bundles" style={{ background: FFR.forest, color: "white" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>Bundles</div>
          <h2 className="font-black uppercase leading-[1.05] tracking-[-0.02em]" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
            Save more when you <span style={{ color: FFR.gold }}>bundle.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {BUNDLES.map((b) => {
            const url = (sc[b.scKey] || "").trim();
            const available = !!url;
            const isFeatured = b.badge === "Best Value";
            return (
              <div
                key={b.key}
                data-testid={`shop-bundle-${b.key}`}
                className="rounded-2xl p-7 border-2 relative flex flex-col"
                style={{
                  background: isFeatured ? "white" : "rgba(255,255,255,0.05)",
                  borderColor: isFeatured ? FFR.gold : `${FFR.gold}44`,
                  color: isFeatured ? FFR.forestDeep : "white",
                }}
              >
                {isFeatured && (
                  <div className="absolute -top-3 left-5 z-10">
                    <div className="text-[10px] uppercase font-black tracking-[0.22em] px-3 py-1.5 rounded shadow-lg" style={{ background: FFR.gold, color: FFR.forestDeep }}>Best Value</div>
                  </div>
                )}
                <Package size={22} style={{ color: isFeatured ? FFR.forest : FFR.gold }} className="mb-3 mt-2" />
                <h3 className="font-black text-[22px] tracking-[-0.02em]" style={{ color: isFeatured ? FFR.forest : "white" }}>{b.name}</h3>
                <p className="text-[13.5px] mt-2 mb-5" style={{ color: isFeatured ? FFR.forestDeep : "rgba(255,255,255,0.7)" }}>{b.desc}</p>
                <div className="font-black text-[46px] leading-none" style={{ color: isFeatured ? FFR.forestDeep : "white" }}>{b.price}</div>
                <div className="text-[12px] mt-1 mb-5" style={{ color: isFeatured ? `${FFR.forestDeep}99` : "rgba(255,255,255,0.55)" }}>Individual license</div>

                {b.includes && b.includes.length > 0 && (
                  <div className="mb-6">
                    <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-2" style={{ color: FFR.gold }}>What&rsquo;s included</div>
                    <ul className="space-y-1.5 text-[13px]" style={{ color: isFeatured ? FFR.forestDeep : "rgba(255,255,255,0.85)" }}>
                      {b.includes.map((line) => (
                        <li key={line} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: FFR.gold }} />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-auto">
                  {b.key === "choose3" ? (
                    <button
                      onClick={() => setChoose3Open(true)}
                      disabled={!available}
                      className="w-full rounded-full py-3.5 font-black uppercase tracking-[0.14em] text-[13px] flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: FFR.gold, color: FFR.forestDeep }}
                      data-testid={`shop-bundle-${b.key}-cta`}
                    >
                      Pick your 3 toolkits
                    </button>
                  ) : (
                    <PurchaseButton stripeUrl={url} available={available} testid={`shop-bundle-${b.key}-cta`} label="Add to cart" productName={b.name} />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* --- Compare bundles table --- */}
        <BundleCompare />
      </div>
      {choose3Open && (
        <ChooseThreeShopModal
          stripeUrl={choose3Url}
          onClose={() => setChoose3Open(false)}
        />
      )}
    </section>
  );
}

/* ---------- Choose Any Three: pre-checkout picker modal ---------- */
const CHOOSE3_TOOLKITS = [
  { sku: "TK1-FUNDING-READINESS", name: "Funding Readiness Toolkit" },
  { sku: "TK2-EVIDENCE-BANK",     name: "Evidence Bank Toolkit" },
  { sku: "TK3-PROGRAM-BLUEPRINT", name: "Program Blueprint Toolkit" },
  { sku: "TK4-BOARD-GOVERNANCE",  name: "Board Governance Toolkit" },
  { sku: "TK5-GRANT-SUCCESS",     name: "Grant Success Toolkit" },
  { sku: "TK6-FUNDRAISING",       name: "Fundraising Toolkit" },
  { sku: "TK8-PROBLEM-STATEMENT", name: "Grant Problem Statement Template" },
];

function ChooseThreeShopModal({ stripeUrl, onClose }) {
  const [picks, setPicks] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const toggle = (sku) => {
    setPicks((p) => {
      if (p.includes(sku)) return p.filter((x) => x !== sku);
      if (p.length >= 3) { toast.error("You can pick up to 3."); return p; }
      return [...p, sku];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (picks.length !== 3) { toast.error("Please pick exactly 3 toolkits."); return; }
    if (!email.includes("@")) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${backend}/api/library/choose-three-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), picks }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Couldn't save your picks");
      }
      // Redirect to Stripe with prefilled email
      const url = new URL(stripeUrl);
      url.searchParams.set("prefilled_email", email.trim().toLowerCase());
      window.location.href = url.toString();
    } catch (err) {
      toast.error(err.message || "Couldn't continue to checkout");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" style={{ background: "rgba(6,34,24,0.85)" }} data-testid="shop-choose3-modal">
      <div className="w-full max-w-lg rounded-3xl p-6 lg:p-8 my-auto" style={{ background: "white" }}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase font-black tracking-[0.22em]" style={{ color: FFR.gold }}>Choose Any Three · $67</div>
            <h3 className="font-black uppercase leading-tight mt-1 tracking-[-0.02em]" style={{ fontSize: "clamp(22px, 2.4vw, 28px)", color: FFR.forest }}>
              Pick your 3 toolkits.
            </h3>
          </div>
          <button onClick={onClose} className="text-[24px] leading-none" style={{ color: FFR.forestDeep }} data-testid="shop-choose3-close" aria-label="Close">×</button>
        </div>
        <p className="text-[13.5px] mb-5" style={{ color: FFR.forestDeep }}>
          They&apos;ll be waiting in your Library the moment your payment clears — no extra steps.
        </p>
        <form onSubmit={submit}>
          <div className="grid sm:grid-cols-2 gap-2.5 mb-5">
            {CHOOSE3_TOOLKITS.map((tk) => {
              const isSelected = picks.includes(tk.sku);
              return (
                <button
                  type="button"
                  key={tk.sku}
                  onClick={() => toggle(tk.sku)}
                  className="text-left rounded-xl px-4 py-3 border-2 transition-all"
                  style={{
                    background: isSelected ? FFR.forest : "white",
                    borderColor: isSelected ? FFR.forestDeep : `${FFR.forest}22`,
                    color: isSelected ? "white" : FFR.forestDeep,
                  }}
                  data-testid={`shop-choose3-pick-${tk.sku}`}
                >
                  <div className="font-black uppercase text-[13px] leading-tight tracking-[-0.01em]">{tk.name}</div>
                  <div className="mt-1 text-[10.5px] uppercase font-bold tracking-[0.12em]">
                    {isSelected ? "✓ Selected" : "Tap to select"}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="text-[12px] font-semibold mb-3" style={{ color: FFR.forestDeep }}>
            {picks.length} of 3 selected
          </div>
          <div className="mb-4">
            <div className="text-[10px] uppercase font-bold tracking-[0.2em] mb-1.5" style={{ color: FFR.forest }}>Your email</div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border-2 py-3 px-4 text-[14.5px] outline-none"
              style={{ borderColor: `${FFR.gold}55`, background: FFR.parchment, color: FFR.forestDeep }}
              data-testid="shop-choose3-email"
            />
            <div className="text-[11.5px] mt-1.5" style={{ color: `${FFR.forestDeep}99` }}>
              Use the same email at Stripe checkout so your picks land in your library automatically.
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || picks.length !== 3 || !stripeUrl}
            className="w-full rounded-full py-3.5 font-black uppercase tracking-[0.14em] text-[13px] flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: FFR.gold, color: FFR.forestDeep }}
            data-testid="shop-choose3-continue"
          >
            {loading ? "Redirecting…" : "Continue to secure checkout →"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------- Compare bundles ---------- */
// Rows describe each product; columns describe each bundle. Values:
//   'pick' → part of the "pick any 3" allotment
//   true   → included
//   false  → not included
const COMPARE_ROWS = [
  { name: "Funding Readiness Toolkit ($27)", choose3: "pick", funding: true,  ultimate: true  },
  { name: "Evidence Bank Toolkit ($27)",      choose3: "pick", funding: true,  ultimate: true  },
  { name: "Program Blueprint Toolkit ($27)",  choose3: "pick", funding: true,  ultimate: true  },
  { name: "Board Governance Toolkit ($27)",   choose3: "pick", funding: false, ultimate: true  },
  { name: "Grant Success Toolkit ($27)",      choose3: "pick", funding: true,  ultimate: true  },
  { name: "Grant Problem Statement Template ($27)", choose3: "pick", funding: false, ultimate: true  },
  { name: "Fundraising Toolkit ($27)",        choose3: "pick", funding: false, ultimate: true  },
  { name: "Nonprofit Operations Toolkit ($97)", choose3: false, funding: false, ultimate: true  },
];

function BundleCompare() {
  const Cell = ({ v }) => {
    if (v === true)   return <span className="inline-flex w-6 h-6 rounded-full items-center justify-center" style={{ background: FFR.gold, color: FFR.forestDeep }}>✓</span>;
    if (v === "pick") return <span className="text-[11px] uppercase font-bold tracking-[0.12em]" style={{ color: FFR.gold }}>Pick&nbsp;any&nbsp;3</span>;
    return <span className="text-white/25 text-[16px]">—</span>;
  };
  const Header = ({ label, price, highlight }) => (
    <th className="text-center py-4 px-3 align-bottom" style={{ borderBottom: `1px solid ${FFR.gold}22` }}>
      <div className={`text-[10px] uppercase font-bold tracking-[0.18em] mb-1 ${highlight ? "" : ""}`} style={{ color: FFR.gold }}>{highlight ? "Best Value" : ""}</div>
      <div className={`font-black uppercase text-[13px] sm:text-[15px] leading-tight ${highlight ? "" : ""}`} style={{ color: "white" }}>{label}</div>
      <div className="text-[13px] mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>{price}</div>
    </th>
  );
  return (
    <div className="mt-16" data-testid="shop-bundle-compare">
      <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>Compare bundles</div>
      <h3 className="font-black uppercase leading-[1.05] tracking-[-0.02em] mb-8" style={{ fontSize: "clamp(22px, 2.6vw, 32px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
        Side-by-side, so you can <span style={{ color: FFR.gold }}>pick with confidence.</span>
      </h3>

      <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: `${FFR.gold}33` }}>
        <table className="w-full min-w-[640px] text-[13.5px]" style={{ color: "white" }}>
          <thead>
            <tr>
              <th className="text-left py-4 px-4 font-bold uppercase text-[10px] tracking-[0.22em]" style={{ color: FFR.gold, background: "rgba(255,255,255,0.03)", borderBottom: `1px solid ${FFR.gold}22` }}>Included</th>
              <Header label="Choose Any Three" price="$67" />
              <Header label="Funding Readiness Bundle" price="$147" />
              <Header label="Ultimate Bundle" price="$197" highlight />
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((row, i) => (
              <tr key={row.name} style={{ background: i % 2 === 1 ? "rgba(255,255,255,0.02)" : "transparent" }}>
                <td className="py-3.5 px-4 border-t" style={{ borderColor: `${FFR.gold}22` }}>{row.name}</td>
                <td className="py-3.5 px-3 text-center border-t" style={{ borderColor: `${FFR.gold}22` }}><Cell v={row.choose3} /></td>
                <td className="py-3.5 px-3 text-center border-t" style={{ borderColor: `${FFR.gold}22` }}><Cell v={row.funding} /></td>
                <td className="py-3.5 px-3 text-center border-t" style={{ borderColor: `${FFR.gold}22`, background: "rgba(201,167,90,0.06)" }}><Cell v={row.ultimate} /></td>
              </tr>
            ))}
            <tr style={{ background: "rgba(201,167,90,0.04)" }}>
              <td className="py-4 px-4 font-bold uppercase text-[10px] tracking-[0.22em] border-t" style={{ color: FFR.gold, borderColor: `${FFR.gold}33` }}>Savings vs à la carte</td>
              <td className="py-4 px-3 text-center border-t text-[13px] font-bold" style={{ borderColor: `${FFR.gold}33` }}>Save $14</td>
              <td className="py-4 px-3 text-center border-t text-[13px] font-bold" style={{ borderColor: `${FFR.gold}33` }}>Save ~$36</td>
              <td className="py-4 px-3 text-center border-t text-[13px] font-black" style={{ borderColor: `${FFR.gold}33`, color: FFR.gold, background: "rgba(201,167,90,0.06)" }}>Save $89</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px]" style={{ color: "rgba(255,255,255,0.5)" }}>
        Choose Any Three lets you pick any 3 of the seven $27 toolkits at checkout. All bundles include an individual, non-transferable license.
      </p>
    </div>
  );
}

/* ---------- Public reviews strip ---------- */
function ReviewsStrip() {
  const [reviews, setReviews] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/public?limit=12`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setReviews(d.reviews || []); })
      .catch(() => { if (!cancelled) setReviews([]); });
    return () => { cancelled = true; };
  }, []);
  if (reviews === null) return null;
  if (reviews.length === 0) return null;
  const avg = reviews.reduce((a, r) => a + r.rating, 0) / reviews.length;
  return (
    <section className="py-20 lg:py-24" data-testid="shop-reviews" style={{ background: FFR.forestDeep, color: "white" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>What buyers say</div>
            <h2 className="font-black uppercase leading-[1.05] tracking-[-0.02em]" style={{ fontSize: "clamp(28px, 3.4vw, 42px)" }}>
              Real reviews from <span style={{ color: FFR.gold }}>real nonprofit leaders.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex" style={{ color: FFR.gold }}>
              {[1,2,3,4,5].map((n) => <Star key={n} size={18} fill={n <= Math.round(avg) ? FFR.gold : "transparent"} strokeWidth={1.5} />)}
            </div>
            <div className="text-[13px] font-bold">{avg.toFixed(1)} · {reviews.length} review{reviews.length === 1 ? "" : "s"}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.slice(0, 6).map((r) => (
            <div key={r.id} className="rounded-2xl p-5 border" style={{ background: "rgba(255,255,255,0.03)", borderColor: `${FFR.gold}33` }}>
              <div className="flex items-center gap-1 mb-2" style={{ color: FFR.gold }}>
                {[1,2,3,4,5].map((n) => <Star key={n} size={13} fill={n <= r.rating ? FFR.gold : "transparent"} strokeWidth={1.5} />)}
              </div>
              <p className="text-[14px] leading-[1.6] text-white/90">&ldquo;{r.body}&rdquo;</p>
              <div className="mt-3 text-[11.5px] flex items-center gap-2" style={{ color: FFR.gold }}>
                <strong>{r.name}</strong>
                {r.verified_purchase && <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border" style={{ borderColor: `${FFR.gold}66`, color: FFR.gold }}>Verified buyer</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Default export ---------- */
export default function Shop() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Resource Shop — Freedom Funding Readiness™";
    return () => { document.title = prev; };
  }, []);
  return (
    <div className="min-h-screen" data-testid="shop-page" style={{ fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif", background: FFR.ivory }}>
      <FfrNav />
      <main>
        <ShopHero />
        <ToolkitsGrid />
        <OperationsFlagship />
        <Bundles />
        <ReviewsStrip />
      </main>
      <FfrFoot />
    </div>
  );
}
